import { ROLE_DESCRIPTIONS, ROLE_LABELS } from './constants';

/**
 * formatLabel — convert a slug-style identifier into a human-readable
 * Title Case string for display.
 *
 *   formatLabel('in_progress')              // 'In Progress'
 *   formatLabel('CORRECTIONS_IN_PROGRESS')  // 'Corrections In Progress'
 *   formatLabel(null)                       // '—'
 *
 * This used to live as a hand-rolled helper inside half a dozen list
 * pages (formatStatusLabel, formatRoleLabel, "snake → title" inline
 * regex calls). Centralizing the logic guarantees the calendar, the
 * lists, the PDF renderer, and the detail pages all render the same
 * status / role / sub-type slugs identically.
 *
 * Empty / nullish input renders as the em-dash placeholder so the
 * column / cell shows the same "no value" marker the rest of the UI
 * uses.
 */
export function formatLabel(value: string | null | undefined): string {
	if (value == null) return '—';
	const trimmed = String(value).trim();
	if (trimmed === '') return '—';
	return trimmed
		.replace(/_+/g, ' ')
		.replace(/\s+/g, ' ')
		.split(' ')
		.filter(Boolean)
		.map((w) => w.charAt(0).toUpperCase() + w.slice(1).toLowerCase())
		.join(' ');
}

/**
 * formatStatusLabel — alias kept for backwards-readability at call sites
 * that specifically want to communicate "this is a status string."
 *
 * Behaviorally identical to formatLabel; the rename gives readers a
 * hint about intent without forcing them to look up the helper.
 */
export const formatStatusLabel = formatLabel;

/**
 * formatRoleLabel — the same Title Case treatment, with an override table for
 * role slugs whose formatted slug reads badly.
 *
 *   formatRoleLabel('company_admin')  // 'Company Admin'
 *   formatRoleLabel('csr')            // 'CSR (Construction Service Rep)'
 *
 * The acronym roles are the reason this exists: formatLabel('csr') renders
 * "Csr", which tells an admin picking a role nothing. Every role label in the
 * app — the Admin user list, the role filter, both create-user forms, the
 * assignee picker — goes through here so a role never reads one way in the
 * picker and another way on the record.
 */
export function formatRoleLabel(slug: string | null | undefined): string {
	if (slug == null) return formatLabel(slug);
	return ROLE_LABELS[String(slug).trim().toLowerCase()] ?? formatLabel(slug);
}

/**
 * formatRoleDescription — the one-line "what this role can do" copy for a role
 * slug, or null when we have nothing to say about it.
 *
 *   formatRoleDescription('reviewer')  // 'Read-only access for auditing...'
 *   formatRoleDescription('mystery')   // null
 *
 * Null rather than a placeholder: a role picker should drop the description
 * line entirely for an unknown slug, not print an em dash under it. Pairs with
 * formatRoleLabel so a role's name and its explanation come from the same
 * place.
 */
export function formatRoleDescription(slug: string | null | undefined): string | null {
	if (slug == null) return null;
	return ROLE_DESCRIPTIONS[String(slug).trim().toLowerCase()] ?? null;
}

/**
 * formatPersonName — a display name from any record carrying a first/last
 * name pair, or null when the record itself is absent.
 *
 * Every eager-loaded person on a detail page arrives as the same
 * `{ firstName, lastName }` shape — assignees, approvers, submitters — and
 * each render site was rebuilding the same guarded template string. That is
 * three ways to get it subtly wrong (an unguarded null, a stray space when
 * only one half is set, an empty string where the caller expected null), so
 * the sites share one implementation instead.
 *
 *   formatPersonName({ firstName: 'Ada', lastName: 'Lovelace' })  // 'Ada Lovelace'
 *   formatPersonName({ firstName: 'Ada' })                        // 'Ada'
 *   formatPersonName(null)                                        // null
 *
 * Returns null rather than '' for an absent or nameless record so callers
 * can fall through to their own placeholder copy with `??`.
 *
 * Accepts the snake_case spellings too — cached/offline snapshots can still
 * hold the raw API shape, while live responses reach the client camelized.
 */
export function formatPersonName(
	person: { firstName?: string | null; lastName?: string | null; first_name?: string | null; last_name?: string | null } | null | undefined
): string | null {
	if (!person) return null;
	const first = person.firstName ?? person.first_name ?? '';
	const last = person.lastName ?? person.last_name ?? '';
	return `${first} ${last}`.trim() || null;
}

/** The user fields formatAssigneeLabel reads (snake_case, as /users returns them). */
export interface AssigneeLabelUser {
	first_name: string;
	last_name: string;
	/** Singular role label on the user record. */
	role?: string | null;
	/** A subcontractor's own business name ("Cyclone Window Cleaning"). */
	subcontractor_company?: string | null;
}

/**
 * Label for a person in an assignee / crew picker, chip or hours row.
 *
 * Internal staff get just their name. Subcontractors are external vendors,
 * so the label says so — and, since Oct 2026, names their business, which is
 * how the office actually knows them (beta feedback: "enter their company
 * name along with their full name"):
 *
 *   formatAssigneeLabel({ first_name: 'Ana', last_name: 'Ruiz' })
 *     // 'Ana Ruiz'
 *   formatAssigneeLabel({ first_name: 'Sam', last_name: 'Rivers', role: 'subcontractor' })
 *     // 'Sam Rivers — Subcontractor'
 *   formatAssigneeLabel({ first_name: 'Sam', last_name: 'Rivers', role: 'subcontractor',
 *                         subcontractor_company: 'Cyclone Window Cleaning' })
 *     // 'Sam Rivers — Cyclone Window Cleaning (Subcontractor)'
 *
 * AssigneeFieldset and TmAssigneeHoursFieldset each carried their own copy of
 * the "— Subcontractor" rule; both use this now.
 */
export function formatAssigneeLabel(u: AssigneeLabelUser): string {
	const name = `${u.first_name ?? ''} ${u.last_name ?? ''}`.trim();
	const company = u.subcontractor_company?.trim();
	if (u.role === 'subcontractor') {
		return company ? `${name} — ${company} (Subcontractor)` : `${name} — Subcontractor`;
	}
	return company ? `${name} — ${company}` : name;
}
